import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, lastValueFrom } from 'rxjs';
import { DataCheckService } from '../../shared/services/data-check.service';
import { gTDB } from '../../shared/services/db';
import { SqliteDbService } from '../../shared/services/sqlite-db.service';
import { NativeUploadUpdateStatus } from '../../../global.variable';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class PagesService {
  // use this for all the needs, no need to create multiple subjects instead use this one and pass dictionary values
  // {
  // this key will be used to pass the updated client count to
  // camera component, so that the user can cancel the upload
  //if there are no clients near him after he takes an image
  //   addPhotoClientCount: value;
  //   sosAlertActive: value;
  //   isOnEvent: value;
  // }
  miscSubjectParam = new Subject();

  isNativeApp = Capacitor.isNativePlatform();

  constructor(
    private http: HttpClient,
    private dataCheckService: DataCheckService,
    private sqliteDb: SqliteDbService,
  ) {}

  refreshTokenApi(isSuperUser: number = 0) {
    return this.http.get('api/refresh_token/', {
      params: { is_super_user: isSuperUser },
    });
  }
  sendToSW(data: any) {
    let payload: any = { type: 'userData' };
    Object.keys(data).forEach((key) => {
      if (['access', 'profile', 'subscriber'].includes(key)) {
        payload[key] = data[key];
      }
    });

    navigator?.serviceWorker?.controller?.postMessage(payload);
  }
  async refreshToken() {
    // check whether token_expiry key is present in localStorage if so, refresh it 5 day before it expires and update localStorage
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const tokenExpiry = userData?.token_expiry;

    if (!tokenExpiry) return false;

    const isSuperUser = this.dataCheckService.isSuperUser() ? 1 : 0;
    const diffDays = this.calculateDateDifference(tokenExpiry);

    if (diffDays > 5) return false;

    const response = await lastValueFrom(this.refreshTokenApi(isSuperUser));
    localStorage.setItem('userData', JSON.stringify(response));
    this.sendToSW(response);

    return response;
  }

  calculateDateDifference(tokenExpiry: any) {
    const date1 = new Date(tokenExpiry);
    const date2 = new Date();

    return Math.floor(
      (date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24),
    );
  }
  getGroupUserCount(dictKey = '2') {
    //  (USER_GROUP_CUSTOM = 0), _('Custom');
    //  (USER_GROUP_MANAGERS = 1), _('Managers');
    //  (USER_GROUP_GUARDS = 2), _('Guards');
    //  (USER_GROUP_DISPATCHERS = 3), _('Dispatchers');
    //  (USER_GROUP_ADMINISTRATOR = 4), _('Administrator');
    let groupUserCount: any = {};
    try {
      groupUserCount = JSON.parse(
        localStorage.getItem('groupUserCount') || '{}',
      );
    } catch (error) {
      groupUserCount = {};
    }

    return groupUserCount?.[dictKey];
  }
  delay(ms: number): Promise<void> {
    // this is for debugging purpose
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  manageS3Urls(body: any, params?: any) {
    return this.http.post('api/manage_s3_files/', body, {
      params: params,
    });
  }
  getPathNameForSavedUpdatesInNativeDb(itemType: string) {
    let pathName = '';
    switch (itemType) {
      case 'job':
        pathName = 'api/job/update_job/';
        break;
      case 'scan_checkpoint':
        pathName = 'api/patrol/scan_checkpoint/';
        break;
      case 'patrol':
        pathName = 'api/patrol/add_update/';
        break;
      case 'register_qr':
        pathName = 'api/check_point/register_qr/';
        break;
      case 'runsheet':
        pathName = 'api/run_sheet/add_update/';
        break;
    }
    return pathName;
  }
  private async getItemsInNativeDb(): Promise<any[]> {
    const queuedItemsInDB: any =
      (await await this.sqliteDb.getAllSavedUpdatesByStatuses([
        NativeUploadUpdateStatus.PENDING,
        NativeUploadUpdateStatus.FAILED,
        // NativeUploadUpdateStatus.IN_PROGRESS,
      ])) ?? [];

    let existingIds = new Set();
    let queuedResposeListData: any[] = [];

    for (const item of queuedItemsInDB) {
      let pathName = this.getPathNameForSavedUpdatesInNativeDb(item?.type);

      const requestData = JSON.parse(item?.data) || {};
      const cachedItem = requestData?.body || {};
      cachedItem.indexDBId = item?.id;
      cachedItem.updated_at = new Date(item?.updatedAt).getTime() / 1000;
      cachedItem.job_id = item?.jobId;
      cachedItem.isInNativeDb = true;
      cachedItem.pathName = pathName;

      cachedItem.event_source = item?.type;
      // if (requestData?.uploadedFilePath) {
      //   cachedItem.file = await this.appService.getDisplayUrlForFileInDevice(
      //     requestData.uploadedFilePath,
      //   );
      // }
      let dataItem: any = { ...cachedItem };

      if (existingIds.has(String(dataItem?.indexDBId))) continue;
      existingIds.add(String(dataItem?.indexDBId));
      if (dataItem?.status == 4 && dataItem?.retryResponse) {
        // had an error
        const errorMessages = JSON.parse(dataItem?.retryResponse);
        const lastError = errorMessages[errorMessages?.length - 1].split(':');
        if (lastError?.[1]) {
          dataItem.errorMessages = [lastError?.[1]];
        }
      }

      queuedResposeListData.push(dataItem);
    }

    return [queuedResposeListData, existingIds];
  }
  private async getItemsInIndexDb(): Promise<any[]> {
    const queuedItemsInDB: any = await gTDB.gtSavedData.toArray();

    let existingIds = new Set();
    let queuedResposeListData: any[] = [];

    const apiResponseDict: any =
      (await gTDB.userData.get('apiResponseDict')) || {};

    for (const item of queuedItemsInDB) {
      let dataItem: any = { ...item };

      if (existingIds.has(String(dataItem?.indexDBId))) continue;
      existingIds.add(String(dataItem?.indexDBId));

      if (apiResponseDict?.[dataItem?.indexDBId]?.errorMessages?.length) {
        dataItem.errorMessages =
          apiResponseDict?.[dataItem?.indexDBId]?.errorMessages;
      }

      queuedResposeListData.push(dataItem);
    }

    return [queuedResposeListData, existingIds];
  }

  async getSavedItemsInClientDb() {
    if (this.isNativeApp) {
      const [queuedResposeNativeListData, existingIdsSet2] =
        await this.getItemsInNativeDb();

      // return [
      //   [...queuedResposeIndexDbListData, ...queuedResposeNativeListData],
      //   [...existingIdsSet1, ...existingIdsSet2],
      // ];queuedResposeNativeListData
      return [queuedResposeNativeListData, existingIdsSet2];
    } else {
      const [queuedResposeIndexDbListData, existingIdsSet1] =
        await this.getItemsInIndexDb();
      return [queuedResposeIndexDbListData, existingIdsSet1];
    }
  }
}
